import React from 'react';

import {useGoToDeclaration} from '../../../../../hooks/use-go-to-declaration';
import {useLocationParams} from '../../../../../hooks/use-location';
import {isDocumentBeingEdited} from '../../../../../utils/document';
import {isReadOnlyOntology} from '../../../../../utils/ontology';
import {OntologyView} from '../../../common/ontology-view';
import {DocumentEditorHeader} from '../document-editor-header';

import {Editor} from './editor';
import {EditorWrapper} from './editor-wrapper';
import {FindUsagesEditorEvent} from '../../../../../../webeditor/biokb-editor-adapters';
import {OntologySidebarWrapper} from './ontology-sidebar-wrapper';

interface Props {
    // eslint-disable-next-line react/no-unused-prop-types
    accession: string;
    onLoadingStateChange: (loading: boolean) => void;
    documentMetadata: DocInfo;
    findUsages: (event: FindUsagesEditorEvent) => void;
}

export function EditorLayout(props: Props) {
    const locationParams = useLocationParams();
    const goToDeclaration = useGoToDeclaration();

    const urlNodeId = locationParams.nodeId && decodeURIComponent(locationParams.nodeId);
    const focusNodeInCurrentDoc = React.useCallback(
        async (nodeId: string) => {
            const isAlreadySelectedNode = urlNodeId === nodeId;
            if (isAlreadySelectedNode) {
                // if node was already selected, we still need to scroll to it,
                // that's why we clear the selection and then set it again;
                // also we use replace here to avoid adding new history entry
                await goToDeclaration(props.accession, null, true);

                setTimeout(() => goToDeclaration(props.accession, nodeId, true), 0);
            }

            goToDeclaration(props.accession, nodeId);
        },
        [goToDeclaration, props.accession, urlNodeId]
    );

    const isEditMode = isDocumentBeingEdited(props.documentMetadata) && !locationParams.revisionId;
    const readonlyOntology = isReadOnlyOntology(props.documentMetadata);

    const editor = (
        <Editor
            isEditMode={isEditMode}
            documentMetadata={props.documentMetadata}
            revisionId={locationParams.revisionId}
            focusedNodeId={urlNodeId}
            onFindUsages={props.findUsages}
            onNavigate={goToDeclaration}
            onLoadingStateChange={props.onLoadingStateChange}
        />
    );

    if (!['ONTOLOGY', 'PROCESSES'].includes(props.documentMetadata.type)) {
        return editor;
    }

    if (readonlyOntology) {
        return (
            <OntologySidebarWrapper
                docInfo={props.documentMetadata}
                focusNode={focusNodeInCurrentDoc}
            >
                <EditorWrapper>
                    <DocumentEditorHeader documentMetadata={props.documentMetadata} />

                    <OntologyView
                        mode="edit"
                        documentMetadata={props.documentMetadata}
                        nodeId={urlNodeId}
                        onFindUsages={props.findUsages}
                        onNavigate={goToDeclaration}
                    />
                </EditorWrapper>
            </OntologySidebarWrapper>
        );
    }

    return (
        <OntologySidebarWrapper docInfo={props.documentMetadata} focusNode={focusNodeInCurrentDoc}>
            {editor}
        </OntologySidebarWrapper>
    );
}
